import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, Unlock, ShieldCheck, AlertTriangle, CalendarClock } from "lucide-react";

export default function PeriodClose() {
  const [status, setStatus] = useState(null);
  const [closeBefore, setCloseBefore] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/accounting/period-status"); setStatus(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const doClose = async () => {
    if (!window.confirm(`سيتم إقفال كل الحركات قبل ${closeBefore} — لا يمكن تعديلها إلا بعد إعادة الفتح. متابعة؟`)) return;
    try { await api.post("/accounting/close-period", { close_before: closeBefore, reason }); toast.success("تم إقفال الفترة"); setReason(""); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const doReopen = async () => {
    if (!window.confirm("إعادة فتح الفترة تسمح بالتعديل على السجلات المُقفلة. متابعة؟")) return;
    try { await api.post("/accounting/reopen-period"); toast.success("تم إعادة الفتح"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const isLocked = !!status?.locked_before;

  return (
    <div className="space-y-4" data-testid="period-page">
      <div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> إقفال الفترة المحاسبية</h1>
        <p className="text-sm text-muted-foreground mt-1">قم بقفل الحركات القديمة لمنع أي تعديل عن طريق الخطأ — يستخدم للإقفال الشهري أو السنوي</p>
      </div>

      <Card className={isLocked ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/40"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {isLocked ? <><Lock className="w-5 h-5 text-emerald-600" /> فترة مُقفَلة</> : <><Unlock className="w-5 h-5 text-amber-600" /> الفترة مفتوحة</>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {isLocked ? (
            <>
              <div>🔒 كل الحركات <b className="text-emerald-700">قبل {status.locked_before}</b> مُقفَلة</div>
              <div className="text-xs text-muted-foreground">
                تم الإقفال في: {status.closed_at && new Date(status.closed_at).toLocaleString("ar-EG")}
                {status.closed_by_name && <> · بواسطة: <b>{status.closed_by_name}</b></>}
              </div>
              {status.reason && <div className="text-xs bg-white/50 p-2 rounded">📝 {status.reason}</div>}
            </>
          ) : (
            <div>لا يوجد إقفال حالياً — كل السجلات قابلة للتعديل من قِبَل المصرّح لهم</div>
          )}
        </CardContent>
      </Card>

      {!isLocked ? (
        <Card>
          <CardHeader><CardTitle className="text-base">إقفال فترة جديدة</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 rounded bg-amber-50 border border-amber-200 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>الإقفال يمنع أي تعديل على الفواتير والسندات وقيود اليومية قبل التاريخ المحدد. يستخدم عادة بعد إتمام التقارير المالية.</div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> إقفال كل شيء قبل تاريخ</Label>
              <Input type="date" value={closeBefore} onChange={e => setCloseBefore(e.target.value)} data-testid="close-date" />
            </div>
            <div><Label>سبب الإقفال (اختياري)</Label><Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="إقفال شهر يناير 2026" data-testid="close-reason" /></div>
            <Button onClick={doClose} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="close-period-btn"><Lock className="w-4 h-4 me-1" /> إقفال الفترة</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base text-red-600">إعادة فتح</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 rounded bg-red-50 border border-red-200 text-xs">
              ⚠️ إعادة الفتح تُلغي القفل بالكامل وتسمح بتعديل السجلات المُقفَلة. يُستخدم فقط لتصحيح أخطاء جسيمة.
            </div>
            <Button onClick={doReopen} variant="outline" className="w-full text-red-600" data-testid="reopen-period-btn"><Unlock className="w-4 h-4 me-1" /> إعادة فتح الفترة</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
